/**
 * Common application types shared across client and server.
 *
 * Types are derived from database Row types where possible to stay in sync.
 */

import type { Camelize } from "bunfx/server";
import type {
  FormRow,
  FormSubmissionRow,
  PriorityRow,
  QuestionRow,
  UserRow,
  WorkspaceMemberRow,
  WorkspaceRow,
} from "../db-schema/public";

// ============================================================================
// Database Enum Types (used by gentypes overrides - must be defined here)
// ============================================================================

export type MemberRole = "admin" | "member";
export type QuestionType = "short_answer" | "rating";
export type BrandColor =
  | "indigo"
  | "blue"
  | "teal"
  | "emerald"
  | "orange"
  | "rose"
  | "purple"
  | "slate";

// ============================================================================
// User Types
// ============================================================================

/** User returned from authentication (excludes timestamps). */
export type User = {
  id: UserRow["id"];
  name: UserRow["name"];
  email: UserRow["email"];
  profilePictureUrl: UserRow["profile_picture_url"];
  emailVerifiedAt?: UserRow["email_verified_at"];
};

// ============================================================================
// Organization Types
// ============================================================================

/** Workspace member with user details (join result). */
export type OrgMember = {
  id: WorkspaceMemberRow["id"];
  role: WorkspaceMemberRow["role"];
  acceptedAt?: WorkspaceMemberRow["accepted_at"];
  archivedAt?: WorkspaceMemberRow["archived_at"];
  leftAt?: WorkspaceMemberRow["left_at"];
  managerId?: WorkspaceMemberRow["manager_id"];
  userId: UserRow["id"];
  userName: UserRow["name"];
  userEmail: UserRow["email"];
  userProfilePictureUrl?: string | null;
};

/** Full organization details including members. */
export type OrganizationDetails = {
  id: WorkspaceRow["id"];
  name: WorkspaceRow["name"];
  logoUrl?: WorkspaceRow["logo_url"];
  timezone: WorkspaceRow["timezone"];
  memberCount: number;
  formCount: number;
  members: OrgMember[];
};

/** Organization list item (summary view). */
export type OrganizationListItem = {
  id: WorkspaceRow["id"];
  name: WorkspaceRow["name"];
  logoUrl?: WorkspaceRow["logo_url"];
  brandColor: BrandColor;
  tenantId: WorkspaceRow["tenant_id"];
  tenantName: string;
  role: MemberRole;
  memberCount: number;
  formCount: number;
  hasReports: boolean;
};

export function isAdmin(role: MemberRole): boolean {
  return role === "admin";
}

/** Check if a member is active (accepted, not archived, not left). */
export function isActiveMember(member: {
  acceptedAt: Date | null;
  archivedAt: Date | null;
  leftAt: Date | null;
}): boolean {
  return (
    member.acceptedAt !== null &&
    member.archivedAt === null &&
    member.leftAt === null
  );
}

// ============================================================================
// Form Types
// ============================================================================

/** Target types for question targeting. */
export type QuestionTargetType = "company_wide" | "group" | "individual";

/** A group for organizing workspace members. */
export type Group = {
  id: string;
  name: string;
  description: string | null;
  createdAt: Date;
  memberCount: number;
};

/** A question within a form (camelized row). */
export type FormQuestion = {
  id: QuestionRow["id"];
  title: QuestionRow["title"];
  description: QuestionRow["description"];
  type: QuestionRow["type"];
  ratingMax: QuestionRow["rating_max"];
  archivedAt: QuestionRow["archived_at"];
  seq: QuestionRow["seq"];
  targetType: QuestionTargetType;
  targetIds: string[];
};

/** Form data (camelized row). */
export type Form = Camelize<FormRow>;

/** Form summary for list views. */
export type FormListItem = {
  id: FormRow["id"];
  title: FormRow["title"];
  questionCount: number;
  archivedAt: FormRow["archived_at"];
  createdAt: FormRow["created_at"];
  updatedAt: FormRow["updated_at"];
};

// ============================================================================
// Form Assignment Types
// ============================================================================

/** A member assigned to a form. */
export type FormAssignee = {
  memberId: WorkspaceMemberRow["id"];
  memberName: UserRow["name"];
  memberEmail: UserRow["email"];
};

// ============================================================================
// Submission Types
// ============================================================================

export type SubmissionStatus = "not_started" | "in_progress" | "submitted";

/** A form assigned to a user (for "My Forms" lists). */
export type AssignedForm = {
  id: FormRow["id"];
  orgId: WorkspaceRow["id"];
  orgName: WorkspaceRow["name"];
  orgLogoUrl: WorkspaceRow["logo_url"];
  title: FormRow["title"];
  status: SubmissionStatus;
  periodStart: FormRow["period_start"];
  periodEnd: FormRow["period_end"];
  questionCount: number;
  /** Whether the form has a recurring schedule */
  isRecurring: boolean;
  /** Submission ID if already submitted for current period */
  submissionId: string | null;
  /** Timestamp when submitted for current period */
  submittedAt: Date | null;
  /** True when the card represents an unsubmitted previous period still inside the grace window */
  isGracePeriod: boolean;
};

/** Lightweight form assignment info (ignores period state). */
export type FormAssignment = {
  id: FormRow["id"];
  title: FormRow["title"];
  periodStart: FormRow["period_start"];
  periodEnd: FormRow["period_end"];
  submissionWindowDays: FormRow["submission_window_days"];
  isRecurring: boolean;
};

/** An answer to a question. */
export type FormAnswer = {
  id?: string; // Only present for saved answers (not during form filling)
  questionId: QuestionRow["id"];
  textAnswer: string | null;
  ratingAnswer: number | null;
  comment?: string | null; // Optional context/explanation for any answer type
};

/** A user's submission for a form period. */
export type FormSubmission = {
  id: FormSubmissionRow["id"];
  formId: FormSubmissionRow["form_id"];
  periodStart: FormSubmissionRow["period_start"];
  submittedAt: FormSubmissionRow["submitted_at"];
  answers: FormAnswer[];
};

/** A past submission for history views. */
export type HistoryItem = {
  id: string;
  formId: string;
  formTitle: string;
  orgId: string;
  orgName: string;
  orgLogoUrl: string | null;
  periodStart: Date;
  submittedAt: Date;
};

/** An org-wide activity item (for admin activity feed). */
export type ActivityItem = {
  id: string;
  formId: string;
  formTitle: string;
  memberId: string;
  memberName: string;
  periodStart: Date;
  submittedAt: Date;
};

// ============================================================================
// Priority Types
// ============================================================================

/** A priority item for tracking tasks across periods. */
export type Priority = {
  id: PriorityRow["id"];
  title: PriorityRow["title"];
  completedAt: PriorityRow["completed_at"];
  carriedFromId: PriorityRow["carried_from_id"];
  seq: PriorityRow["seq"];
  /** Original creation date if carried multiple times (lineage tracking) */
  originDate?: Date;
  /** Number of times this priority has been carried (1 = carried once) */
  carryCount?: number;
};

/** Priority with member context (for manager view). */
export type PriorityWithMember = Priority & {
  memberName: string;
  memberId: string;
};

// ============================================================================
// Comment & Reaction Types
// ============================================================================

/** Target types that can have comments and reactions. */
export type CommentTargetType =
  | "priority"
  | "form_answer"
  | "high_five"
  | "value_recognition";

/** A comment on a priority or form answer. */
export type Comment = {
  id: string;
  content: string;
  createdAt: Date;
  authorMemberId: string;
  authorName: string;
  authorProfilePictureUrl: string | null;
};

/** Summary of reactions for a single emoji. */
export type ReactionSummary = {
  emoji: string;
  count: number;
  memberNames: string[];
  userReacted: boolean;
};

/** Notification types. */
export type NotificationType =
  | "mention"
  | "comment"
  | "reaction"
  | "everyone"
  | "recognition";

/** A notification for the user's notification stream. */
export type Notification = {
  id: string;
  workspaceId: string;
  type: string;
  title: string;
  body: string | null;
  targetType: string | null;
  targetId: string | null;
  viewUrl: string;
  readAt: Date | null;
  createdAt: Date;
  actorMemberId: string | null;
  actorName: string | null;
  actorProfilePictureUrl: string | null;
};

// ============================================================================
// Core Value Types
// ============================================================================

/** A core value defined for a workspace. */
export type CoreValue = {
  id: string;
  name: string;
  tagline: string;
  description: string;
  sortOrder: number;
};

/** A value recognition entry during a check-in. */
export type ValueRecognition = {
  id: string;
  workspaceId: string;
  message: string;
  periodStart: Date;
  createdAt: Date;
  author: {
    memberId: string;
    name: string;
    profilePictureUrl: string | null;
  };
  recipients: {
    memberId: string;
    name: string;
    profilePictureUrl: string | null;
  }[];
  values: {
    id: string;
    name: string;
  }[];
};

// ============================================================================
// High Five Types
// ============================================================================

/** A high five recognition entry. */
export type HighFive = {
  id: string;
  workspaceId: string;
  message: string;
  createdAt: Date;
  author: {
    memberId: string;
    name: string;
    profilePictureUrl: string | null;
  };
  recipients: {
    memberId: string;
    name: string;
    profilePictureUrl: string | null;
  }[];
};

/** Page size for high fives feed. */
export const HIGH_FIVE_PAGE_SIZE = 20;

// ============================================================================
// Member Profile Types
// ============================================================================

/** A member's public profile. */
export type MemberProfile = {
  memberId: string;
  name: string;
  email: string;
  title: string | null;
  bio: string | null;
  role: MemberRole;
  profilePictureUrl: string | null;
  managerName: string | null;
  managerId: string | null;
  directReportCount: number;
  // Viewer relationship
  isSelf: boolean;
  isManager: boolean;
  isAdmin: boolean;
};

// ============================================================================
// Constants
// ============================================================================

/** Default page size for submission history pagination. */
export const HISTORY_PAGE_SIZE = 5;

/** Default page size for org activity pagination. */
export const ACTIVITY_PAGE_SIZE = 20;

// ============================================================================
// Form Results Types (Admin Aggregated View)
// ============================================================================

/** Default number of text responses to show initially. */
export const TEXT_RESPONSES_INITIAL_LIMIT = 5;

// ============================================================================
// Question Bank Types
// ============================================================================

/** A reusable question template in the question bank. */
export type BankQuestion = {
  id: string;
  title: string;
  description: string;
  type: QuestionType;
  ratingMax: number;
  category: string | null;
  usageCount: number;
  archivedAt: Date | null;
  createdAt: Date;
};

/** A period for which form submissions exist. */
export type FormResultsPeriod = {
  start: Date;
  end: Date | null;
};

/** Basic question info for results view. */
export type FormResultsQuestion = {
  id: string;
  title: string;
  type: QuestionType;
  ratingMax: number;
};

/** Rating aggregate for a question. */
export type FormResultsRating = {
  questionId: string;
  responseCount: number;
  averageRating: number;
};

/** A text response with metadata. */
export type FormResultsTextResponse = {
  questionId: string;
  text: string;
  submittedAt: Date;
  memberName: string;
  profilePictureUrl: string | null;
};

/** Form results for admin view - raw data for client to combine. */
export type FormResults = {
  form: { id: string; title: string };
  orgId: string;
  orgName: string;
  periods: FormResultsPeriod[];
  period: FormResultsPeriod | null;
  submissionCount: number;
  questions: FormResultsQuestion[];
  ratings: FormResultsRating[];
  textResponses: FormResultsTextResponse[];
};
